import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/auth';
import { useOnboardingStatus } from '@/hooks/use-onboarding-status';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2,
  User,
  Briefcase,
  Settings,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Shield,
  Phone
} from 'lucide-react';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

type OnboardingStep = 'welcome' | 'identity' | 'role' | 'contact' | 'preferences' | 'complete';

const STEPS: OnboardingStep[] = ['welcome', 'identity', 'role', 'contact', 'preferences', 'complete'];

const PROFESSIONAL_CATEGORIES = [
  { value: 'radiologist_attending', label: 'Attending Radiologist' },
  { value: 'trainee', label: 'Radiology Trainee / Resident / Fellow' },
  { value: 'non_clinical', label: 'Non-Clinical (Admin, Research, Other)' },
];

const PRIMARY_ROLES = [
  { value: 'diagnostic_radiologist', label: 'Diagnostic Radiologist' },
  { value: 'trainee', label: 'Trainee / Resident' },
  { value: 'admin', label: 'Administrator' },
  { value: 'evaluator', label: 'Evaluator / Demo User' },
];

const INTENDED_USES = [
  { value: 'clinical_production', label: 'Clinical Production' },
  { value: 'non_production', label: 'Non-Production (Testing, Development)' },
  { value: 'education_only', label: 'Education Only' },
];

const PRACTICE_SETTINGS = [
  { value: 'academic', label: 'Academic' },
  { value: 'private_practice', label: 'Private Practice' },
  { value: 'teleradiology', label: 'Teleradiology' },
  { value: 'hospital_employed', label: 'Hospital Employed' },
  { value: 'training_program', label: 'Training Program' },
  { value: 'other', label: 'Other' },
];

const REFERRAL_SOURCES = [
  { value: 'colleague', label: 'Colleague or Word of Mouth' },
  { value: 'conference', label: 'Conference' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'search', label: 'Search Engine' },
  { value: 'publication', label: 'Publication' },
  { value: 'other', label: 'Other' },
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
  'France', 'India', 'Japan', 'Brazil', 'Other'
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshBootstrap } = useAuth();
  const { status, identityCompleted, roleCompleted, contactCompleted, refetch } = useOnboardingStatus();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [loading, setLoading] = useState(false);

  // Form state - Identity
  const [legalName, setLegalName] = useState('');
  const [professionalCategory, setProfessionalCategory] = useState('');
  const [countryOrRegion, setCountryOrRegion] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [practiceSetting, setPracticeSetting] = useState('');
  const [city, setCity] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [attestation, setAttestation] = useState(false);

  // Form state - Role
  const [primaryRole, setPrimaryRole] = useState('');
  const [intendedUse, setIntendedUse] = useState('');

  // Form state - Contact
  const [phoneNumber, setPhoneNumber] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [npiNumber, setNpiNumber] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Form state - Preferences
  const [reportVerbosity, setReportVerbosity] = useState('conservative');
  const [narrativeStyle, setNarrativeStyle] = useState('standard');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Note: We no longer auto-redirect on status === 'complete'
  // Instead, we show the completion step and let the user click "Go to Dashboard"
  // This prevents the redirect loop caused by stale AuthProvider state

  // Resume from last step (or show complete if already finished)
  useEffect(() => {
    if (status === 'complete') {
      setCurrentStep('complete');
    } else if (identityCompleted && !roleCompleted) {
      setCurrentStep('role');
    } else if (identityCompleted && roleCompleted && !contactCompleted) {
      setCurrentStep('contact');
    } else if (identityCompleted && roleCompleted && contactCompleted) {
      setCurrentStep('preferences');
    }
  }, [status, identityCompleted, roleCompleted, contactCompleted]);

  const stepIndex = STEPS.indexOf(currentStep);
  const progress = ((stepIndex) / (STEPS.length - 1)) * 100;

  const handleIdentitySubmit = async () => {
    if (!legalName.trim() || !professionalCategory || !countryOrRegion || !institutionName.trim() || !practiceSetting || !attestation) {
      toast({
        variant: 'destructive',
        title: 'Required fields',
        description: 'Please fill in all required fields and accept the attestation.',
      });
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await supabase.functions.invoke('onboarding-identity', {
        body: {
          legal_name: legalName.trim(),
          professional_category: professionalCategory,
          country_or_region: countryOrRegion,
          institution_name: institutionName.trim(),
          practice_setting: practiceSetting,
          city: city.trim() || null,
          state_region: stateRegion.trim() || null,
          attestation: true,
        },
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to save identity');
      }

      await refetch();
      setCurrentStep('role');
      toast({
        title: 'Identity saved',
        description: 'Your identity information has been recorded.',
      });
    } catch (err) {
      console.error('[Onboarding] Identity error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save identity information',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSubmit = async () => {
    if (!primaryRole || !intendedUse) {
      toast({
        variant: 'destructive',
        title: 'Required fields',
        description: 'Please select your role and intended use.',
      });
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await supabase.functions.invoke('onboarding-role', {
        body: {
          primary_role: primaryRole,
          intended_use: intendedUse,
        },
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to save role');
      }

      await refetch();
      setCurrentStep('contact');
      toast({
        title: 'Role saved',
        description: 'Your role information has been recorded.',
      });
    } catch (err) {
      console.error('[Onboarding] Role error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save role information',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async () => {
    if (!referralSource) {
      toast({
        variant: 'destructive',
        title: 'Required fields',
        description: 'Please tell us how you heard about us.',
      });
      return;
    }

    if (npiNumber.trim() && !/^[0-9]{10}$/.test(npiNumber.trim())) {
      toast({
        variant: 'destructive',
        title: 'Invalid NPI',
        description: 'NPI number must be exactly 10 digits.',
      });
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await supabase.functions.invoke('onboarding-contact', {
        body: {
          phone: phoneNumber.trim() || null,
          referral_source: referralSource,
          marketing_opt_in: marketingOptIn,
          npi_number: npiNumber.trim() || null,
        },
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to save contact information');
      }

      await refetch();
      setCurrentStep('preferences');
      toast({
        title: 'Contact information saved',
        description: 'Your contact details have been recorded.',
      });
    } catch (err) {
      console.error('[Onboarding] Contact error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save contact information',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesSubmit = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      
      // Save preferences (optional)
      await supabase.functions.invoke('onboarding-preferences', {
        body: {
          report_verbosity: reportVerbosity,
          narrative_style: narrativeStyle,
        },
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      // Complete onboarding
      const response = await supabase.functions.invoke('onboarding-complete', {
        body: {},
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to complete onboarding');
      }

      await refetch();
      // Refresh AuthProvider bootstrap to update gatingReason from ONBOARDING_REQUIRED to PENDING_APPROVAL
      // This prevents the redirect loop between OnboardingGate and this page
      await refreshBootstrap();
      setCurrentStep('complete');
      toast({
        title: 'Onboarding complete!',
        description: 'Your account is now pending admin approval.',
      });
    } catch (err) {
      console.error('[Onboarding] Complete error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to complete onboarding',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPreferences = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      
      // Complete onboarding without saving preferences
      const response = await supabase.functions.invoke('onboarding-complete', {
        body: {},
        headers: {
          Authorization: `Bearer ${session.session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to complete onboarding');
      }

      await refetch();
      // Refresh AuthProvider bootstrap to update gatingReason from ONBOARDING_REQUIRED to PENDING_APPROVAL
      // This prevents the redirect loop between OnboardingGate and this page
      await refreshBootstrap();
      setCurrentStep('complete');
      toast({
        title: 'Onboarding complete!',
        description: 'Your account is now pending admin approval.',
      });
    } catch (err) {
      console.error('[Onboarding] Skip preferences error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to complete onboarding',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex flex-col">
      {/* Header */}
      <div className="p-6 flex items-center gap-3">
        <RadiologyLiveIcon size={32} />
        <span className="font-semibold text-lg">RadiologyLive.ai</span>
      </div>

      {/* Progress */}
      {currentStep !== 'welcome' && currentStep !== 'complete' && (
        <div className="px-6 max-w-2xl mx-auto w-full">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Identity</span>
            <span>Role</span>
            <span>Contact</span>
            <span>Preferences</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          {/* Welcome Step */}
          {currentStep === 'welcome' && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4">
                  <RadiologyLiveIcon size={64} />
                </div>
                <CardTitle className="text-2xl">Welcome to RadiologyLive.ai</CardTitle>
                <CardDescription className="text-base">
                  AI-powered radiology dictation that learns your voice
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                <div className="space-y-4 text-sm text-muted-foreground">
                  <p>
                    Before you can start using RadiologyLive.ai, we need to collect some 
                    information about you. This helps us:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <User className="h-4 w-4 mt-0.5 text-foreground" />
                      <span>Verify your professional identity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Briefcase className="h-4 w-4 mt-0.5 text-foreground" />
                      <span>Configure appropriate access levels</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Settings className="h-4 w-4 mt-0.5 text-foreground" />
                      <span>Personalize your experience</span>
                    </li>
                  </ul>
                  <p className="pt-2">
                    This process takes about 2 minutes.
                  </p>
                </div>
                <Button 
                  onClick={() => setCurrentStep('identity')} 
                  className="w-full"
                  size="lg"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {/* Identity Step */}
          {currentStep === 'identity' && (
            <>
              <CardHeader>
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <User className="h-5 w-5" />
                  <span className="text-sm font-medium">Step 1 of 4</span>
                </div>
                <CardTitle>Identity Verification</CardTitle>
                <CardDescription>
                  Please provide your professional identity information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="legalName">Full Legal Name *</Label>
                  <Input
                    id="legalName"
                    placeholder="Dr. Jane Smith"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="professionalCategory">Professional Category *</Label>
                  <Select value={professionalCategory} onValueChange={setProfessionalCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFESSIONAL_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="countryOrRegion">Country/Region *</Label>
                  <Select value={countryOrRegion} onValueChange={setCountryOrRegion}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="institutionName">Institution *</Label>
                  <Input
                    id="institutionName"
                    placeholder="General Hospital / Radiology Associates"
                    value={institutionName}
                    onChange={(e) => setInstitutionName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="practiceSetting">Practice Setting *</Label>
                  <Select value={practiceSetting} onValueChange={setPracticeSetting}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your practice setting" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRACTICE_SETTINGS.map((setting) => (
                        <SelectItem key={setting.value} value={setting.value}>
                          {setting.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="City (optional)"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stateRegion">State/Region</Label>
                    <Input
                      id="stateRegion"
                      placeholder="State/Region (optional)"
                      value={stateRegion}
                      onChange={(e) => setStateRegion(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-4 p-4 bg-muted rounded-lg">
                  <Checkbox
                    id="attestation"
                    checked={attestation}
                    onCheckedChange={(checked) => setAttestation(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="attestation" className="text-sm font-medium cursor-pointer">
                      I attest that the information provided is accurate *
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      I confirm that I am authorized to use medical dictation software and 
                      that the information I have provided is true and accurate.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('welcome')}
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleIdentitySubmit}
                    disabled={loading || !legalName || !professionalCategory || !countryOrRegion || !institutionName.trim() || !practiceSetting || !attestation}
                    className="flex-1"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Role Step */}
          {currentStep === 'role' && (
            <>
              <CardHeader>
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Briefcase className="h-5 w-5" />
                  <span className="text-sm font-medium">Step 2 of 4</span>
                </div>
                <CardTitle>Role & Intended Use</CardTitle>
                <CardDescription>
                  Tell us how you plan to use RadiologyLive.ai
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryRole">Primary Role *</Label>
                  <Select value={primaryRole} onValueChange={setPrimaryRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your primary role" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIMARY_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {primaryRole === 'trainee' && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Note: Trainees cannot sign or export reports for clinical use.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="intendedUse">Intended Use *</Label>
                  <Select value={intendedUse} onValueChange={setIntendedUse}>
                    <SelectTrigger>
                      <SelectValue placeholder="How will you use RadiologyLive.ai?" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTENDED_USES.map((use) => (
                        <SelectItem key={use.value} value={use.value}>
                          {use.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Shield className="h-4 w-4 text-primary" />
                    Access Control
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Your role determines what features you can access. Capabilities are 
                    assigned during admin approval based on your declared role.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('identity')}
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleRoleSubmit}
                    disabled={loading || !primaryRole || !intendedUse}
                    className="flex-1"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Contact Step */}
          {currentStep === 'contact' && (
            <>
              <CardHeader>
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Phone className="h-5 w-5" />
                  <span className="text-sm font-medium">Step 3 of 4</span>
                </div>
                <CardTitle>Contact & Verification</CardTitle>
                <CardDescription>
                  Help us stay in touch and verify your credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Mobile phone (optional)</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referralSource">How did you hear about us? *</Label>
                  <Select value={referralSource} onValueChange={setReferralSource}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a source" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRAL_SOURCES.map((source) => (
                        <SelectItem key={source.value} value={source.value}>
                          {source.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="npiNumber">NPI Number (optional, US only)</Label>
                  <Input
                    id="npiNumber"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit NPI"
                    value={npiNumber}
                    onChange={(e) => setNpiNumber(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                  <p className="text-xs text-muted-foreground">
                    We use this to verify your credentials automatically.
                  </p>
                </div>

                <div className="flex items-start space-x-3 pt-4 p-4 bg-muted rounded-lg">
                  <Checkbox
                    id="marketingOptIn"
                    checked={marketingOptIn}
                    onCheckedChange={(checked) => setMarketingOptIn(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="marketingOptIn" className="text-sm font-medium cursor-pointer">
                      Keep me informed about new RadiologyLive features and offers
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Optional. You can unsubscribe at any time.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('role')}
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleContactSubmit}
                    disabled={loading || !referralSource}
                    className="flex-1"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Preferences Step */}
          {currentStep === 'preferences' && (
            <>
              <CardHeader>
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Settings className="h-5 w-5" />
                  <span className="text-sm font-medium">Step 4 of 4</span>
                </div>
                <CardTitle>Preferences</CardTitle>
                <CardDescription>
                  Configure your report preferences (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reportVerbosity">Report Verbosity</Label>
                  <Select value={reportVerbosity} onValueChange={setReportVerbosity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative (Concise)</SelectItem>
                      <SelectItem value="moderate">Moderate (Balanced)</SelectItem>
                      <SelectItem value="detailed">Detailed (Comprehensive)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="narrativeStyle">Narrative Style</Label>
                  <Select value={narrativeStyle} onValueChange={setNarrativeStyle}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="structured">Structured (Bulleted)</SelectItem>
                      <SelectItem value="prose">Prose (Paragraph)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-xs text-muted-foreground pt-2">
                  These preferences can be changed later in your account settings.
                </p>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('contact')}
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleSkipPreferences}
                    disabled={loading}
                  >
                    Skip
                  </Button>
                  <Button
                    onClick={handlePreferencesSubmit}
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Complete Setup
                        <CheckCircle2 className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Complete Step */}
          {currentStep === 'complete' && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle>Setup Complete!</CardTitle>
                <CardDescription>
                  Your account is now pending admin approval
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Thank you for completing the onboarding process. Our team will review 
                  your application and activate your account.
                </p>
                <p className="text-xs text-muted-foreground">
                  This typically takes 1-2 business days. You'll be notified when your 
                  account is ready.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/app/livespeak')}
                  className="mt-4"
                >
                  Go to Dashboard
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
